import { Injectable, signal, computed } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Observable, of, tap } from 'rxjs';
import { DataRepositoryService } from 'src/app/shared/services/data-repository/data-repository.service';
import { DataAccessService } from 'src/app/shared/services/data-access/data-access.service';
import { DeleteListPaylaod, UserPreferencesService } from 'src/app/shared/services/user-preferences/user-preferences.service';
import { SelectionOption } from 'src/app/shared/entity/utility-interfaces';
import { LocalStorage } from 'src/app/shared/entity/enums';
import { UserPreferenceEnum } from '../setting/setting.component';
import { RoutePreferenceModel } from 'src/app/shared/models/route-preference';
import { AppConstants } from 'src/app/shared/entity/constants';
import { ConnectionService } from 'src/app/shared/services/connection/connection.service';
import { ErrorMessage, ErrorType } from 'src/app/shared/entity/error.const';

@Injectable({
  providedIn: 'root'
})
export class CustomRoutePreferenceService {

  showRoutePreferenceForm = signal<boolean>(false);
  editingPreference = signal<RoutePreferenceModel | null>(null);
  blList = signal<SelectionOption[]>([]);
  routesList = signal<SelectionOption[]>([]);
  routePreferences = signal<RoutePreferenceModel[]>([]);
  selectedBlIdForFilter = signal<string>('');

  /**
   * Computed signal that filters route preferences by the selected business location ID.
   */
  filteredRoutePreferences = computed(() => {
    const blId = this.selectedBlIdForFilter();
    const preferences = this.routePreferences();
    if (!blId) return [];
    return preferences.filter(p => p.blId === blId);
  });

  /**
   * Returns preference options for dropdown (filtered by BL).
   */
  filteredPreferenceOptions = computed<SelectionOption[]>(() => {
    return this.filteredRoutePreferences().map(p => ({
      id: p.id,
      value: p.name
    }));
  });

  constructor(
    private readonly dataRepositoryService: DataRepositoryService,
    private readonly dataAccessService: DataAccessService,
    private readonly connectionService: ConnectionService,
    private readonly userPreferenceService: UserPreferencesService,
    private readonly messageService: MessageService
  ) {}

  /**
   * Loads business locations from cache or API.
   */
  loadBusinessLocations() {
    if (this.blList().length) return;
    if (this.dataRepositoryService.businessLocationListMap.size) {
      this.blList.set(
        [...this.dataRepositoryService.businessLocationListMap.values()].map(data => ({
          id: data.business_location_id,
          value: data.name
        }))
      );
    } else {
      this.dataAccessService.getBLData().subscribe(res => {
        this.blList.set([...res]);
      });
    }
  }

  /**
   * Loads routes for the selected business location using pamroutes API.
   */
  loadRoutes(blId: string): void {
    this.routesList.set([]);
    if (!blId) return;
    this.connectionService.httpReq({
      reqType: AppConstants.GETREQUEST,
      endUrl: `${AppConstants.BUSINESSLOCATIONS}/${blId}/${AppConstants.PAMROUTES}`,
      directoryType: AppConstants.V4
    }).subscribe((res: any) => {
      if (res?.pamroutes) {
        // Sort pamroutes by route_name (alphanumeric)
        const sortedRoutes = res.pamroutes.slice().sort((a: any, b: any) => {
          // Compare by route_name as string, natural order
          
          return String(a?.route_name ?? '').localeCompare(String(b?.route_name ?? ''), undefined, { numeric: true, sensitivity: 'base' });
        });
        this.routesList.set(
          sortedRoutes.map((route: any) => ({
            id: String(route?.route_name ?? ''),
            value: String(route?.route_name ?? ''),
          }))
        );
      }
    });
}

  /**
   * Fetches all custom route preferences from the global key.
   * Returns an Observable so callers can wait for completion.
   */
  getRoutePreferences(): Observable<RoutePreferenceModel[]> {
    return this.userPreferenceService.getUserPreferenceByKey(UserPreferenceEnum.GLOBAL_Filter_PREFERENCE).pipe(
      tap((response: any) => {
        const parsed = response?.preferenceValue ? JSON.parse(response.preferenceValue) : [];
        const data = Array.isArray(parsed) ? parsed : [parsed];
        this.routePreferences.set(data);
      })
    ) as Observable<RoutePreferenceModel[]>;
  }

  /**
   * Resolves the BL name from the blList or businessLocationListMap.
   */
  private resolveBlName(blId: string): string {
    const fromList = this.blList().find(bl => bl.id === blId);
    if (fromList) return fromList.value;
    const fromMap = this.dataRepositoryService.businessLocationListMap.get(blId);
    return fromMap?.name ?? blId;
  }

  /**
   * Saves a custom route preference by adding/updating it in the global array.
   */
  saveRoutePreference(preference: RoutePreferenceModel) {
    // Ensure blName is populated
    if (!preference.blName) {
      preference.blName = this.resolveBlName(preference.blId);
    }
    // Update the local array of preferences
    const currentPreferences = [...this.routePreferences()];
    // Check if preference already exists (edit) or is new (create) and update array accordingly
    const existingIndex = currentPreferences.findIndex(p => p.id === preference.id);
    if (existingIndex >= 0) {
      currentPreferences[existingIndex] = preference;
    } else {
      currentPreferences.push(preference);
    }

    this.userPreferenceService.saveUserPreference(
      UserPreferenceEnum.GLOBAL_Filter_PREFERENCE,
      currentPreferences
    ).subscribe({
      next: () => {
        const message = `Custom route preference '${preference.name}' saved successfully!`;
        this.routePreferences.set(currentPreferences);
        this.showRoutePreferenceForm.set(false);
        this.editingPreference.set(null);
        // If the edited preference was the selected one in the filter, update localStorage and refresh
        if (existingIndex >= 0) {
          const storedPref = localStorage.getItem(LocalStorage.SelectedUserPreference);
          if (storedPref) {
            const parsed = JSON.parse(storedPref);
            const storedId = typeof parsed === 'object' ? parsed?.id : parsed;
            if (storedId === preference.id) {
              localStorage.setItem(LocalStorage.SelectedUserPreference, JSON.stringify({ id: preference.id, routes: preference.routes }));
              this.dataAccessService.setFilterApplied(true, { isRouteChanged: true });
            }
          }
        }
        this.messageService.add({ severity: ErrorType.SUCCESS, closable: false, detail: message });
      },
      error: () => {
        this.messageService.add({ severity: ErrorType.ERROR, closable: false, detail: ErrorMessage.BASEERRORMESSAGE });
      }
    });
  }

  /**
   * Opens the form for creating a new preference.
   */
  createNewPreference() {
    this.editingPreference.set(null);
    this.routesList.set([]);
    this.showRoutePreferenceForm.set(true);
  }

  /**
   * Opens the form for editing an existing preference.
   */
  editPreference(preference: RoutePreferenceModel) {
    this.editingPreference.set(preference);
    this.showRoutePreferenceForm.set(true);
    this.loadRoutes(preference.blId);
  }

  /**
   * Deletes a route preference by removing it from the global array and re-saving.
   * Clears the selected preference if the deleted one was active.
   */
  deleteRoutePreference(preference: RoutePreferenceModel) {
    const payload: DeleteListPaylaod = {
      preferenceKey: UserPreferenceEnum.GLOBAL_Filter_PREFERENCE,
      id: preference.id
    };

    this.userPreferenceService.deleteUserPreference(payload).subscribe({
      next: () => {
        this.routePreferences.set(this.routePreferences().filter(p => p.id !== preference.id));
        // Clear from localStorage if the deleted preference was selected
        const storedPref = localStorage.getItem(LocalStorage.SelectedUserPreference);
        if (storedPref) {
          const parsed = JSON.parse(storedPref);
          const storedId = typeof parsed === 'object' ? parsed?.id : parsed;
          if (storedId === preference.id) {
            localStorage.removeItem(LocalStorage.SelectedUserPreference);
            // Refresh routeinfo API without the deleted preference routes
            this.dataAccessService.setFilterApplied(true, { isRouteChanged: true });
          }
        }
        this.messageService.add({ severity: ErrorType.SUCCESS, closable: false, detail: `Route preference '${preference.name}' deleted successfully!` });
      },
      error: () => {
        this.messageService.add({ severity: ErrorType.ERROR, closable: false, detail: ErrorMessage.BASEERRORMESSAGE });
      }
    });
  }

}
